import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  fetchActowizFeatures,
  fetchActowizProducts,
  fetchActowizTopDeals,
} from "../store/actowiz/actowizSlice";

const chips = [
  { id: "offer", label: "Offer" },
  { id: "base", label: "Base" },
  { id: "loyalty", label: "Loyalty + Offer" },
  { id: "tesco", label: "Tesco" },
  { id: "sainsbury", label: "Sainsbury’s" },
  { id: "asda", label: "Asda" },
  { id: "morrison", label: "Morrisons" },
];

const RETAIL_KEYS = new Set(["tesco", "asda", "morrison", "sainsbury"]);
const QUICK_OFFSET = 12;

const PLATFORM_LABELS = {
  tesco: "Tesco",
  asda: "ASDA",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

function quickCompareRequestKey(chip) {
  const o = QUICK_OFFSET;
  switch (chip) {
    case "offer":
      return `features_v2:0:${o}`;
    case "loyalty":
      return `top_deals::0:${o}`;
    case "base":
      return `all:0:${o}`;
    case "tesco":
      return `tesco:0:${o}`;
    case "sainsbury":
      return `sainsbury:0:${o}`;
    case "asda":
      return `asda:0:${o}`;
    case "morrison":
      return `morrison:0:${o}`;
    default:
      return "";
  }
}

export default function HomeQuickCompareSection({
  title = "Quick Compare",
  onAddToList,
  onSeeAll,
  onOpenProduct,
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeChip, setActiveChip] = useState(chips[0].id);

  const features = useAppSelector((s) => s.actowiz.features);
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const unfilteredProducts = useAppSelector((s) => s.actowiz.unfilteredProducts);
  const requests = useAppSelector((s) => s.actowiz.requests);

  useEffect(() => {
    const arg = { start: 0, offset: QUICK_OFFSET, mode: "replace", force: true };
    switch (activeChip) {
      case "offer":
        dispatch(fetchActowizFeatures(arg));
        break;
      case "base":
        dispatch(fetchActowizProducts({ ...arg }));
        break;
      case "loyalty":
        dispatch(fetchActowizTopDeals(arg));
        break;
      case "tesco":
        dispatch(fetchActowizProducts({ ...arg, platform: "tesco" }));
        break;
      case "sainsbury":
        dispatch(fetchActowizProducts({ ...arg, platform: "sainsbury" }));
        break;
      case "asda":
        dispatch(fetchActowizProducts({ ...arg, platform: "asda" }));
        break;
      case "morrison":
        dispatch(fetchActowizProducts({ ...arg, platform: "morrison" }));
        break;
      default:
        break;
    }
  }, [activeChip, dispatch]);

  const reqKey = quickCompareRequestKey(activeChip);
  const isLoading = requests?.[reqKey] === "loading";

  const compareItems = useMemo(() => {
    const pool = activeChip === "offer" ? features : activeChip === "loyalty" ? topDeals : [];
    return (pool || []).filter(
      (p) => p?.title && Array.isArray(p?.comparison) && p.comparison.length > 0
    );
  }, [activeChip, features, topDeals]);

  const productItems = useMemo(() => {
    if (activeChip === "loyalty") {
      return (topDeals || []).filter((p) => p?.title).slice(0, 12);
    }
    if (activeChip === "base") {
      return (unfilteredProducts || []).filter((p) => p?.title).slice(0, 12);
    }
    if (RETAIL_KEYS.has(activeChip)) {
      const raw = byPlatform?.[activeChip] || [];
      return (Array.isArray(raw) ? raw : []).filter((p) => p?.title).slice(0, 12);
    }
    return [];
  }, [activeChip, topDeals, unfilteredProducts, byPlatform]);

  // "Offer" uses compare-style cards, but "Loyalty + Offer" should look like product listings.
  const showCompare = activeChip === "offer";
  const gridItems = showCompare ? compareItems.slice(0, 12) : productItems;

  const handleSeeAll = () => {
    if (typeof onSeeAll === "function") {
      onSeeAll(activeChip);
      return;
    }
    if (activeChip === "offer") navigate("/features");
    else if (activeChip === "loyalty") navigate("/top-deals");
    else if (activeChip === "base") navigate("/products");
    else if (RETAIL_KEYS.has(activeChip)) navigate(`/platform/${activeChip}`);
  };

  const openProduct = (product) => {
    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? "";
    if (!productId) return;
    if (typeof onOpenProduct === "function") {
      onOpenProduct(product, activeChip);
      return;
    }
    if (showCompare) {
      navigate(`/product/${encodeURIComponent(productId)}`);
      return;
    }
    if (activeChip === "loyalty") {
      const platformKey = normalizeStoreKey(product?.platform) || "tesco";
      navigate(
        `/product-store/${encodeURIComponent(platformKey)}/${encodeURIComponent(productId)}`,
        { state: { product } }
      );
      return;
    }
    if (activeChip === "base") {
      const plat = String(product?.platform || "").toLowerCase();
      const p =
        plat.includes("tesco")
          ? "tesco"
          : plat.includes("asda")
          ? "asda"
          : plat.includes("morris")
          ? "morrison"
          : plat.includes("sains")
          ? "sainsbury"
          : "tesco";
      navigate(`/product-store/${encodeURIComponent(p)}/${encodeURIComponent(productId)}`);
      return;
    }
    if (RETAIL_KEYS.has(activeChip)) {
      navigate(
        `/product-store/${encodeURIComponent(activeChip)}/${encodeURIComponent(productId)}`,
        { state: { product } }
      );
    }
  };

  return (
    <section className="rounded-2xl bg-transparent">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[20px] font-semibold text-[#24303A]">{title}</h2>
        <button
          type="button"
          onClick={handleSeeAll}
          className="rounded-full bg-[#7352C7] px-8 py-1.5 text-[14px] font-bold text-white shadow-sm"
        >
          See All
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-3">
        {chips.map((chip) => {
          const active = chip.id === activeChip;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => setActiveChip(chip.id)}
              className={
                active
                  ? "rounded-full border-2 border-[#7352C7] bg-[#ECE8FA] px-6 py-1.5 text-[14px] font-bold text-[#7352C7]"
                  : "rounded-full border-2 border-[#E6EAF0] bg-white px-6 py-1.5 text-[14px] font-bold text-[#6E7B86] hover:bg-slate-50"
              }
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="mt-8 flex justify-center">
          <span className="text-[14px] text-[#6E7B86]">Loading…</span>
        </div>
      )}

      {!isLoading && (
        <div className="mt-4 grid gap-8 md:grid-cols-3">
          {showCompare &&
            compareItems.slice(0, 12).map((product, idx) => {
              const id = product?.product_id ?? product?.pid ?? product?.sku ?? String(idx);
              return (
                <ProductCard
                  key={`${id}-${idx}`}
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  onAddToList={onAddToList}
                  showComparison
                  onClick={() => openProduct(product)}
                />
              );
            })}
          {!showCompare &&
            productItems.map((product, idx) => {
              const id = product?.product_id ?? product?.pid ?? product?.sku ?? String(idx);
              const label =
                activeChip === "base"
                  ? PLATFORM_LABELS[normalizeStoreKey(product?.platform)] || "Store"
              : activeChip === "loyalty"
              ? PLATFORM_LABELS[normalizeStoreKey(product?.platform)] || "Store"
              : PLATFORM_LABELS[activeChip] || "Store";
              return (
                <ProductCard
                  key={`${id}-${idx}`}
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  onAddToList={onAddToList}
                  showComparison={false}
                  retailerLabel={label}
                  onClick={() => openProduct(product)}
                />
              );
            })}
        </div>
      )}

      {!isLoading && !gridItems.length && (
        <p className="mt-6 text-center text-[14px] text-[#6E7B86]">No products to show for this tab.</p>
      )}
    </section>
  );
}

function normalizeStoreKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("asda")) return "asda";
  if (p.includes("morris")) return "morrison";
  if (p.includes("sains")) return "sainsbury";
  return "";
}
